// @ts-strict-ignore
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { Org, fetchOrg as apiFetchOrg } from 'api/orgs';
import { setError } from 'store/main/ducks/error';

export type MainStoreOrgsStateOrg = Org & {
  isFetching: boolean;
  isUnpublished: boolean;
};

export type MainStoreOrgsState = {
  byId: Record<string, Partial<MainStoreOrgsStateOrg>>;
};

export const fetchOrg = createAsyncThunk<Org, string>(
  'orgs/fetchOrg',
  // eslint-disable-next-line consistent-return
  async (orgId: string, thunkAPI) => {
    try {
      const fetchedOrg = await apiFetchOrg(orgId);
      return fetchedOrg as MainStoreOrgsStateOrg;
    } catch (e) {
      if (e.statusCode !== 404) {
        thunkAPI.dispatch(setError(e));
      } else {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        thunkAPI.dispatch(setOrgUnpublished(orgId));
      }
    }
  }
);

export const { actions, reducer } = createSlice({
  name: 'orgs',
  initialState: {
    byId: {},
  },
  reducers: {
    setOrgUnpublished: (state: MainStoreOrgsState, action) => {
      state.byId[action.payload].isFetching = false;
      state.byId[action.payload].isUnpublished = true;
    },
    updateOrg: (state, action) => {
      state.byId[action.payload.id] = action.payload;
    },
    partialUpdateOrg: (state, action) => ({
      ...state,
      byId: {
        ...state.byId,
        [action.payload.id]: {
          ...state.byId[action.payload.id],
          ...action.payload,
        },
      },
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrg.pending, (state: MainStoreOrgsState, action) => {
        state.byId[action.meta.arg] = {
          id: action.meta.arg,
          isFetching: true,
        };
      })
      .addCase(fetchOrg.fulfilled, (state, action) => {
        state.byId[action.meta.arg].isFetching = false;

        // If a listing is unpublished, the action technically
        // is fulfilled, but there is no payload, so we
        // don't set it here, letting the `setOrgUnpublished`
        // action handle the state change.
        if (action.payload) {
          state.byId[action.meta.arg] = action.payload;
        }
      });
  },
});

export const { updateOrg, setOrgUnpublished, partialUpdateOrg } = actions;
